import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CloudDownload as SyncIcon,
  Refresh as RefreshIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import api from '@services/api';
import { invalidateSportModulesCache } from '@hooks/useSportModules';
import {
  STATE_COLORS,
  STATE_LABELS,
  type SportModuleMatrixData,
  type SportModuleState,
} from '@utils/sportModules';

// Re-export valid states for select (manifest uses same list as backend)
const MODULE_STATES: SportModuleState[] = [
  'disabled',
  'coming_soon',
  'admin_only',
  'beta',
  'public',
];

interface AdminMatrixPayload extends SportModuleMatrixData {
  manifestMatrix?: Record<string, Record<string, string>>;
  manifestVersion?: string;
}

const authHeaders = () => {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminSportModuleRollout: React.FC = () => {
  const [data, setData] = useState<AdminMatrixPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<Record<string, SportModuleState>>({});
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const res = await api.get('/admin/sport-module-settings', { headers: authHeaders() });
      setData(res.data?.data);
      setPending({});
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : 'Failed to load rollout matrix');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cellKey = (sport: string, moduleId: string) => `${sport}::${moduleId}`;

  const getCellState = (sport: string, moduleId: string): SportModuleState => {
    const key = cellKey(sport, moduleId);
    if (pending[key]) return pending[key];
    return data?.matrix?.[sport]?.[moduleId]?.state ?? 'disabled';
  };

  const manifestDiffCount = useMemo(() => {
    if (!data?.manifestMatrix) return 0;
    let n = 0;
    for (const sport of data.sports || []) {
      for (const mod of data.modules || []) {
        const db = data.matrix?.[sport]?.[mod.id]?.state ?? 'disabled';
        const manifest = data.manifestMatrix[sport]?.[mod.id] ?? 'disabled';
        if (db !== manifest) n += 1;
      }
    }
    return n;
  }, [data]);

  const queueChange = (sport: string, moduleId: string, state: SportModuleState) => {
    const key = cellKey(sport, moduleId);
    setPending((prev) => ({ ...prev, [key]: state }));
  };

  const saveChanges = async () => {
    const updates = Object.entries(pending).map(([key, state]) => {
      const [sport, moduleId] = key.split('::');
      return { sport, moduleId, state };
    });
    if (updates.length === 0) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await api.put(
        '/admin/sport-module-settings',
        { updates },
        { headers: authHeaders() }
      );
      setData(res.data?.data);
      setPending({});
      invalidateSportModulesCache();
      setMessage(`Saved ${updates.length} change(s). Live without redeploy.`);
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const syncManifest = async (overwrite: boolean) => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await api.post(
        '/admin/sport-module-settings/sync-manifest',
        { overwrite },
        { headers: authHeaders() }
      );
      setData(res.data?.data);
      setPending({});
      invalidateSportModulesCache();
      setMessage(res.data?.message || 'Synced from manifest');
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return <Alert severity="error">{message || 'No rollout data'}</Alert>;
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
            Sport Module Rollout
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Runtime matrix (DB). Defaults: <code>config/sport-module-defaults.json</code> v{data.manifestVersion || data.version}.
            Developers run <code>npm run sport-modules:status</code> before deploy.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={saving}>
            Refresh
          </Button>
          <Button
            startIcon={<SyncIcon />}
            variant="outlined"
            onClick={() => syncManifest(false)}
            disabled={saving}
          >
            Seed missing from manifest
          </Button>
          <Tooltip title="Overwrites all DB cells with manifest — use with care">
            <Button
              startIcon={<ResetIcon />}
              color="warning"
              variant="outlined"
              onClick={() => {
                if (window.confirm('Reset entire matrix from manifest? Admin overrides will be lost.')) {
                  syncManifest(true);
                }
              }}
              disabled={saving}
            >
              Reset from manifest
            </Button>
          </Tooltip>
          <Button variant="contained" onClick={saveChanges} disabled={saving || Object.keys(pending).length === 0}>
            Save {Object.keys(pending).length > 0 ? `(${Object.keys(pending).length})` : ''}
          </Button>
        </Stack>
      </Stack>

      {message && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      {manifestDiffCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {manifestDiffCount} cell(s) differ from git manifest — expected after admin overrides. Review before next deploy.
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, minWidth: 120 }}>Sport</TableCell>
              {data.modules.map((mod) => (
                <TableCell key={mod.id} sx={{ fontWeight: 700, minWidth: 130 }}>
                  <Typography variant="caption" display="block" sx={{ fontWeight: 800 }}>
                    {mod.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {mod.id}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.sports.map((sport) => (
              <TableRow key={sport} hover>
                <TableCell sx={{ fontWeight: 700 }}>{sport}</TableCell>
                {data.modules.map((mod) => {
                  const state = getCellState(sport, mod.id);
                  const manifestState = data.manifestMatrix?.[sport]?.[mod.id];
                  const differs = manifestState && manifestState !== state;
                  const isPending = Boolean(pending[cellKey(sport, mod.id)]);
                  return (
                    <TableCell key={mod.id} sx={{ bgcolor: isPending ? '#fffbeb' : undefined }}>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={state}
                          onChange={(e) => queueChange(sport, mod.id, e.target.value as SportModuleState)}
                        >
                          {MODULE_STATES.map((s) => (
                            <MenuItem key={s} value={s}>
                              {STATE_LABELS[s]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                        <Chip label={STATE_LABELS[state]} size="small" color={STATE_COLORS[state]} />
                        {differs && (
                          <Chip label={`git: ${manifestState}`} size="small" variant="outlined" color="warning" />
                        )}
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AdminSportModuleRollout;
